import {
  Button,
  Checkbox,
  Group,
  Modal,
  Radio,
  Stack,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";

import { TodoConstants } from "constant";

import DateTimeSelect, { ChevronMultiSelect, ChevronSelect } from "./DateTimeSelect";
import PriorityCheckbox from "./PriorityCheckbox";
import { useTodoForm } from "./useTodoForm";
import { useUserOptions } from "./useUserOptions";

const { RECURRING_TYPE_LABELS } = TodoConstants;

interface NewTodoModalProps {
  opened: boolean;
  onClose: () => void;
}

const NewTodoModal = ({ opened, onClose }: NewTodoModalProps) => {
  // Form state and logic
  const {
    formData,
    dateTime,
    endDateTime,
    errors,
    updateFormData,
    updateDateTime,
    updateEndDateTime,
    clearError,
    resetForm,
    validateIntervalErrors,
    handleSubmit,
    handleClose,
  } = useTodoForm({ onClose });

  // User options for dropdowns
  const { employeeSelectOptions, clientSelectOptions } = useUserOptions({
    opened,
    onModalClose: resetForm,
  });

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      size={900}
      centered
      withCloseButton={false}
      padding="xl"
      radius="md"
    >
      <Stack gap="md">
        <Title order={4} fz={18}>
          Novi To Do
        </Title>

        <Group align="stretch" gap="xl">
          {/* Left side - form fields */}
          <Stack gap="md" style={{ flex: 2 }}>
            <TextInput
              placeholder="Naslov..."
              radius="md"
              size="sm"
              value={formData.title}
              onChange={(e) => {
                updateFormData({ title: e.target.value });
                clearError("title");
              }}
              error={errors.title}
            />
            <Group wrap="nowrap">
              <span style={{ width: 100, flexShrink: 0, fontSize: 14 }}>Za:</span>
              <ChevronMultiSelect
                placeholder={formData.assignees.length === 0 ? "Izaberi" : undefined}
                data={employeeSelectOptions}
                value={formData.assignees}
                onChange={(value) => {
                  updateFormData({ assignees: value });
                  clearError("assignees");
                }}
                flex={1}
                radius="md"
                size="sm"
                error={errors.assignees}
              />
            </Group>
            <Group wrap="nowrap">
              <span style={{ width: 100, flexShrink: 0, fontSize: 14 }}>Klijent:</span>
              <ChevronSelect
                placeholder="Izaberi klijenta"
                data={clientSelectOptions}
                value={formData.client}
                onChange={(value) => updateFormData({ client: value })}
                clearable
                flex={1}
                radius="md"
                size="sm"
                error={errors.client}
              />
            </Group>
            {formData.assignees.length > 1 && (
              <Group wrap="nowrap">
                <span style={{ width: 100, flexShrink: 0, fontSize: 14 }}>Mora uraditi:</span>
                <Radio.Group
                  value={formData.assigneeType}
                  onChange={(value) => updateFormData({ assigneeType: value })}
                >
                  <Group>
                    <Radio
                      value="everyone"
                      label="Svako"
                      size="xs"
                      styles={{
                        label: { fontSize: 14, cursor: "pointer" },
                        radio: { cursor: "pointer" },
                      }}
                    />
                    <Radio
                      value="anyone"
                      label="Bilo ko"
                      size="xs"
                      styles={{
                        label: { fontSize: 14, cursor: "pointer" },
                        radio: { cursor: "pointer" },
                      }}
                    />
                  </Group>
                </Radio.Group>
              </Group>
            )}
            <Group wrap="nowrap">
              <span style={{ width: 100, flexShrink: 0, fontSize: 14 }}>Vremenski tip:</span>
              <Radio.Group
                value={formData.timeType}
                onChange={(value) => updateFormData({ timeType: value })}
              >
                <Group>
                  <Radio
                    value="deadline"
                    label="Rok"
                    size="xs"
                    styles={{
                      label: { fontSize: 14, cursor: "pointer" },
                      radio: { cursor: "pointer" },
                    }}
                  />
                  <Radio
                    value="exact"
                    label="Tačno vreme"
                    size="xs"
                    styles={{
                      label: { fontSize: 14, cursor: "pointer" },
                      radio: { cursor: "pointer" },
                    }}
                  />
                  <Radio
                    value="interval"
                    label="Period"
                    size="xs"
                    styles={{
                      label: { fontSize: 14, cursor: "pointer" },
                      radio: { cursor: "pointer" },
                    }}
                  />
                </Group>
              </Radio.Group>
            </Group>

            {(formData.timeType === "deadline" || formData.timeType === "exact") && (
              <Group wrap="nowrap">
                <span style={{ width: 100, flexShrink: 0, fontSize: 14 }}>Datum i vreme:</span>
                <DateTimeSelect
                  day={dateTime.day}
                  month={dateTime.month}
                  year={dateTime.year}
                  time={dateTime.time}
                  onDayChange={(value) => {
                    updateDateTime({ day: value });
                    clearError("day");
                  }}
                  onMonthChange={(value) => {
                    updateDateTime({ month: value });
                    clearError("month");
                  }}
                  onYearChange={(value) => {
                    updateDateTime({ year: value });
                    clearError("year");
                  }}
                  onTimeChange={(value) => {
                    updateDateTime({ time: value });
                    clearError("time");
                  }}
                  dayError={errors.day}
                  monthError={errors.month}
                  yearError={errors.year}
                  timeError={errors.time}
                />
              </Group>
            )}
            {formData.timeType === "interval" && (
              <>
                <Group wrap="nowrap">
                  <span style={{ width: 100, flexShrink: 0, fontSize: 14 }}>Početak:</span>
                  <DateTimeSelect
                    day={dateTime.day}
                    month={dateTime.month}
                    year={dateTime.year}
                    time={dateTime.time}
                    onDayChange={(value) => {
                      updateDateTime({ day: value });
                      updateEndDateTime({ day: value }); // Auto-copy to end date
                      clearError("day");
                      clearError("endDay");
                    }}
                    onMonthChange={(value) => {
                      updateDateTime({ month: value });
                      updateEndDateTime({ month: value }); // Auto-copy to end date
                      clearError("month");
                      clearError("endMonth");
                    }}
                    onYearChange={(value) => {
                      updateDateTime({ year: value });
                      updateEndDateTime({ year: value }); // Auto-copy to end date
                      clearError("year");
                      clearError("endYear");
                    }}
                    onTimeChange={(value) => {
                      const newDateTime = { ...dateTime, time: value };
                      updateDateTime({ time: value });
                      clearError("time");
                      validateIntervalErrors(newDateTime, endDateTime);
                    }}
                    dayError={errors.day}
                    monthError={errors.month}
                    yearError={errors.year}
                    timeError={errors.time}
                  />
                </Group>
                <Group wrap="nowrap">
                  <span style={{ width: 100, flexShrink: 0, fontSize: 14 }}>Kraj:</span>
                  <DateTimeSelect
                    day={endDateTime.day}
                    month={endDateTime.month}
                    year={endDateTime.year}
                    time={endDateTime.time}
                    onDayChange={(value) => {
                      const newEndDateTime = { ...endDateTime, day: value };
                      updateEndDateTime({ day: value });
                      validateIntervalErrors(dateTime, newEndDateTime);
                    }}
                    onMonthChange={(value) => {
                      const newEndDateTime = { ...endDateTime, month: value };
                      updateEndDateTime({ month: value });
                      validateIntervalErrors(dateTime, newEndDateTime);
                    }}
                    onYearChange={(value) => {
                      const newEndDateTime = { ...endDateTime, year: value };
                      updateEndDateTime({ year: value });
                      validateIntervalErrors(dateTime, newEndDateTime);
                    }}
                    onTimeChange={(value) => {
                      const newEndDateTime = { ...endDateTime, time: value };
                      updateEndDateTime({ time: value });
                      validateIntervalErrors(dateTime, newEndDateTime);
                    }}
                    dayError={errors.endDay}
                    monthError={errors.endMonth}
                    yearError={errors.endYear}
                    timeError={errors.endTime}
                  />
                </Group>
              </>
            )}
            <Checkbox
              label="Ponavljajući zadatak"
              size="xs"
              styles={{ label: { fontSize: 14, cursor: "pointer" }, input: { cursor: "pointer" } }}
              checked={formData.recurring}
              onChange={(e) => {
                updateFormData({
                  recurring: e.target.checked,
                  recurringType: e.target.checked ? "DAILY" : null,
                });
              }}
            />
            {formData.recurring && (
              <Radio.Group
                value={formData.recurringType}
                onChange={(value) => updateFormData({ recurringType: value })}
                ml="md"
              >
                <Group gap="md">
                  {(Object.keys(RECURRING_TYPE_LABELS) as Array<keyof typeof RECURRING_TYPE_LABELS>).map(
                    (type) => (
                      <Radio
                        key={type}
                        value={type}
                        label={RECURRING_TYPE_LABELS[type]}
                        size="xs"
                        styles={{
                          label: { fontSize: 14, cursor: "pointer" },
                          radio: { cursor: "pointer" },
                        }}
                      />
                    ),
                  )}
                </Group>
              </Radio.Group>
            )}
            <Textarea
              placeholder="Detaljnije..."
              autosize
              minRows={3}
              maxLength={300}
              radius="md"
              size="sm"
              value={formData.description}
              onChange={(e) => updateFormData({ description: e.target.value })}
            />
          </Stack>

          {/* Right side - type, priority and buttons */}
          <Stack gap="md" justify="space-between" style={{ flex: 1 }}>
            <Stack gap="md">
              <ChevronSelect
                placeholder="Tip"
                data={["Poziv", "Sastanak", "Interni zadatak"]}
                radius="md"
                size="sm"
                value={formData.todoType}
                onChange={(value) => {
                  updateFormData({ todoType: value });
                  clearError("todoType");
                }}
                error={errors.todoType}
              />
              <Stack gap="xs" align="flex-end">
                <PriorityCheckbox
                  label="Hitno"
                  checked={formData.urgent}
                  onChange={(value) => updateFormData({ urgent: value })}
                />
                <PriorityCheckbox
                  label="Važno"
                  checked={formData.important}
                  onChange={(value) => updateFormData({ important: value })}
                />
              </Stack>
            </Stack>
            <Group justify="flex-end" gap="xl">
              <Button
                variant="outline"
                color="dark"
                radius="md"
                size="sm"
                fz={14}
                onClick={handleClose}
              >
                Otkaži
              </Button>
              <Button color="green" radius="md" size="sm" fz={14} onClick={handleSubmit}>
                Sačuvaj
              </Button>
            </Group>
          </Stack>
        </Group>
      </Stack>
    </Modal>
  );
};

export default NewTodoModal;
